import React, { useState, useEffect } from 'react';
import emailjs from 'emailjs-com';
import { FaPaperPlane } from 'react-icons/fa';
import { FiMail, FiPhone, FiMapPin, FiClock } from 'react-icons/fi';
import './Contact.css';

/**
 * Contact.jsx
 * 
 * EmailJS Setup (Quick):
 * 1) In EmailJS, create a Service and a Template with variables:
 *    from_name, reply_to, message, to_email
 * 2) Copy your Service ID, Template ID, and Public Key and set them below.
 * 3) In the EmailJS template, make sure the "To" field uses: {{to_email}}
 * 4) Install dependency (already in package.json): npm install emailjs-com
 */

// Provided EmailJS credentials
const SERVICE_ID = 'service_eisrq1o';
const TEMPLATE_ID = 'template_2qm7zgo';
const PUBLIC_KEY = '_p_h0ecpiBMrM49bw';

// All submissions will be sent to this inbox
const RECEIVER_EMAIL = 'cvbenergy@gmail.com';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '' });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setAlert({ type: 'danger', message: 'Please fill in all fields.' });
      return false;
    }
    const emailValid = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(form.email);
    if (!emailValid) {
      setAlert({ type: 'danger', message: 'Please enter a valid email address.' });
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (alert.message) {
      const t = setTimeout(() => setAlert({ type: '', message: '' }), 4000);
      return () => clearTimeout(t);
    }
  }, [alert.message]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setAlert({ type: '', message: '' });
    if (!validate()) return;

    setSubmitting(true);
    try {
      const templateParams = {
        // Primary keys used in the template
        from_name: form.name,
        reply_to: form.email,
        message: form.message,
        to_email: RECEIVER_EMAIL,
        // Common alternates (in case your template uses different variable names)
        user_name: form.name,
        user_email: form.email,
        from_email: form.email,
        name: form.name,
        email: form.email,
        subject: `New message from ${form.name}`,
      };

      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);

      setAlert({ type: 'success', message: 'Message sent successfully!' });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to send message. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="contact" className="contact-container">
      <div className="contact-header">
        <h1>Get In Touch</h1>
        <p>We’d love to hear from you! <br />
For inquiries, partnerships, or consultations — our experts are ready to help.
</p>
      </div>

      {/* Top fixed alert */}
      <div className={`top-alert-container ${alert.message ? 'show' : ''}`} aria-live="assertive" aria-atomic="true">
        {alert.message && (
          <div className={`alert alert-${alert.type} shadow-sm mb-0`} role="alert">
            {alert.message}
          </div>
        )}
      </div>

      <div className="contact-body">
        <div className="contact-info">
          <h2>Contact Information</h2>
          <p>Reach out to us through any of these channels, and we'll respond promptly.</p>

          <div className="info-item">
            <FiMail className="icon" />
            <div>
              <h3>Email</h3>
              <p><a href="mailto:cvbenergy@gmail.com">cvbenergy@gmail.com</a></p>
            </div>
          </div>

          <div className="info-item">
            <FiPhone className="icon" />
            <div>
              <h3>Phone</h3>
              <p><a href="tel:+918144240999">+91 8144240999</a></p>
            </div>
          </div>

          <div className="info-item">
            <FiMapPin className="icon" />
            <div>
              <h3>Location</h3>
              <p>4/751 Nehur nagar, Trichy road, <br /> Vasanthapuram post,Namakkal </p>
            </div>
          </div>

          <div className="info-item">
            <FiClock className="icon" />
            <div>
              <h3>Business Hours</h3>
              <p>Mon - Fri: 9:00 AM – 6:00 PM</p>
              <p>Saturday: 10:00 AM – 4:00 PM</p>
              <p>Sunday: Closed</p>
            </div>
          </div>
        </div>

        <div className="contact-form">
          <h2>Send us a message</h2>
          <form onSubmit={onSubmit} noValidate>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Your Name"
              value={form.name}
              onChange={onChange}
              disabled={submitting}
              required
            />

            <input
              id="email"
              name="email"
              type="email"
              placeholder="Your Email"
              value={form.email}
              onChange={onChange}
              disabled={submitting}
              required
            />

            <textarea
              id="message"
              name="message"
              placeholder="Your Message"
              rows="5"
              value={form.message}
              onChange={onChange}
              disabled={submitting}
              required
            />

            <button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Sending...
                </>
              ) : (
                <>
                  Send Message <FaPaperPlane />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
